import { Dispatch } from "react";
import axios from "axios";
import jwtAxios from "../../auth/jwtAxios";
import { AppActions } from "../../types";
import { TeamMemberProgress, UserProgress } from "../../types/models/TeamMember";
import {
  GET_TEAM_MEMBERS,
  GET_USERS,
  GET_ORG_TEAMS,
  SEARCH_ORG_TEAMS,
  GET_MY_TEAMS,
  GET_PROGRESS_OF_TEAM,
  GET_MY_PROGRESS,
  GET_PROGRESS_OF_USERS,
  GET_MY_UNRESPONDED_MESSAGE_COUNT,
  GET_ALL_USERS,
  UPDATE_USER_OPT_IN_ROLES,
  UPDATE_USER_LOCATIONS,
} from "../../types/actions/TeamMembers.actions";
import { fetchStart, fetchSuccess, fetchError, showMessage } from "./Common";
import { VirtualTeam } from "../../types/models/Team";
import { RISUser } from "../../types/models/RISUser";
import { MakeTeamRequest } from "../../types/models/MakeTeamRequest";
import { MakeTeamResponse } from "../../types/models/MakeTeamResponse";
import { UpdateTeamRequest } from "../../types/models/UpdateTeamRequest";
import { TeamsSearchResult } from "../../types/models/TeamsSearchResult";
import { getDisplayErrorMessage, normalizeDate, splitSearchText } from "../../utils/StringUtils";
import { Acknowledgment } from "../../types/models/Acknowledgement";
import { UserFitnessStatus } from "../../types/models/UserFitnessStatus";
import { UserSkill } from "../../types/models/UserSkillCoverage";
import { UserSkillItem } from "../../types/models/UserSkillItem";
import { CSVExportStatusResponse } from "../../types/models/CSVExportStatusResponse";
import { OrgUser } from "../../pages/user-administration/OrgUser";

export const getOrgProgress = (
  page: number,
  limit = 20,
  callBack?: (
    teamMembers: TeamMemberProgress[] | undefined,
    hasNext: boolean | undefined,
    totalCount: number | undefined
  ) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get(`api/TeamProgress?pageIndex=${page}&pageSize=${limit}`)
      .then((data) => {
        if (data.status === 200) {
          dispatch(fetchSuccess());
          const teamMembers: TeamMemberProgress[] = data.data.Values;
          const hasNext = data.data.HasNext;
          const totalCount = data.data.TotalCount;

          dispatch({ type: GET_TEAM_MEMBERS, payload: teamMembers });
          callBack && callBack(teamMembers, hasNext, totalCount);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack && callBack(undefined, undefined, undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack && callBack(undefined, undefined, undefined);
      });
  };
};

export const getTeamProgress = (
  teamId: string,
  page: number,
  limit = 20,
  callBack?: (
    progress: TeamMemberProgress[] | undefined,
    hasNext: boolean | undefined,
    totalCount: number | undefined
  ) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get(`api/ProgressOfTeam?teamId=${teamId}&pageIndex=${page}&pageSize=${limit}`)
      .then((data) => {
        if (data.status === 200) {
          dispatch(fetchSuccess());
          const progress: TeamMemberProgress[] = data.data.Values;
          const hasNext = data.data.HasNext;
          const totalCount = data.data.TotalCount;

          dispatch({ type: GET_PROGRESS_OF_TEAM, payload: progress });
          callBack && callBack(progress, hasNext, totalCount);
        } else {
          dispatch(
            fetchError("There was a problem loading the team. Please try again or contact support.")
          );
          callBack && callBack(undefined, undefined, undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack && callBack(undefined, undefined, undefined);
      });
  };
};

export const getOrgTeams = (callBack?: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("api/orgteams")
      .then((data) => {
        if (data.status === 200) {
          const teams: VirtualTeam[] = data.data.Teams;
          // sorting
          teams?.forEach((team) => {
            team.Members = team.Members?.sort((a, b) => {
              const aName =
                (a.DisplayName && a.DisplayName.length > 0 ? a.DisplayName : a.Email) ?? "";
              const bName =
                (b.DisplayName && b.DisplayName.length > 0 ? b.DisplayName : b.Email) ?? "";
              return aName.localeCompare(bName);
            });
          });
          dispatch({ type: GET_ORG_TEAMS, payload: teams });
          callBack && callBack(true);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack && callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack && callBack(false);
      });
  };
};

export const getUsers = (callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("api/mysubordinates")
      .then((data) => {
        if (data.status === 200) {
          const users: RISUser[] = data.data.Subordinates;
          dispatch({ type: GET_USERS, payload: users });
          callBack && callBack(true);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack && callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack && callBack(false);
      });
  };
};

export const getPotentialTeamMembers = (callback?: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/potentialteammembers")
      .then((data) => {
        if (data.status === 200) {
          const users: RISUser[] = data.data.Subordinates;
          dispatch({ type: GET_USERS, payload: users });
          callback && callback(true);
        } else {
          dispatch(
            fetchError(
              `Team member list is currently unavailable. Please check again later or contact support. Status Code: ${data.status}`
            )
          );
          callback && callback(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(false);
      });
  };
};

export const makeTeam = (
  makeRequest: MakeTeamRequest,
  callBack: (teamId: string | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("api/maketeam", makeRequest)
      .then((data) => {
        if (data.status === 200) {
          const response: MakeTeamResponse = data.data;
          callBack(response.TeamId);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(undefined);
      });
  };
};

export const updateTeam = (
  updateRequest: UpdateTeamRequest,
  callBack: (result: boolean) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("api/updateteam", updateRequest)
      .then((data) => {
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(false);
      });
  };
};

export const deleteTeam = (teamId: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("api/deleteteam", { TeamId: teamId })
      .then((data) => {
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(false);
      });
  };
};

export const searchOrgTeams = (searchText: string, baseOrgTeams: VirtualTeam[]) => {
  return (dispatch: Dispatch<AppActions>) => {
    let result: TeamsSearchResult;
    if (searchText) {
      const searches = splitSearchText(searchText);
      const filteredOrgTeams = baseOrgTeams.filter((team) => {
        let result = true;
        searches.forEach((search) => {
          result =
            result &&
            (team.DisplayName.toLowerCase().includes(search) ||
              team.Members.some(
                (member) =>
                  member.DisplayName.toLowerCase().includes(search) ||
                  member.Email.toLowerCase().includes(search)
              ));
        });
        return result;
      });
      result = {
        SearchResults: filteredOrgTeams,
        SearchText: searchText.trim(),
      };
    } else {
      result = {
        SearchResults: baseOrgTeams,
        SearchText: "",
      };
    }
    dispatch({ type: SEARCH_ORG_TEAMS, payload: result });
  };
};

export const getMyTeams = (callBack?: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("api/myteams")
      .then((data) => {
        if (data.status === 200) {
          const teams: VirtualTeam[] = data.data.Teams;
          dispatch({ type: GET_MY_TEAMS, payload: teams });
          callBack && callBack(true);
        } else {
          dispatch(
            fetchError(
              "There was a problem loading your teams. Please try again or contact support."
            )
          );
          callBack && callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack && callBack(false);
      });
  };
};

export const getMyProgress = (callback?: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/MyProgress")
      .then((response) => {
        if (response.status === 200) {
          const progress: UserProgress = response.data;
          dispatch({ type: GET_MY_PROGRESS, payload: progress });
          callback && callback(true);
        } else {
          dispatch(
            fetchError(
              "There was a problem loading your progress. Please try again or contact support."
            )
          );
          callback && callback(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(false);
      });
  };
};

export const getProgressOfUsers = (
  userIds: string[],
  callback?: (progress: Acknowledgment[] | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/ProgressOfUsers", { userIds })
      .then((response) => {
        if (response.status === 200) {
          const progress: Acknowledgment[] = response.data.Values;
          dispatch({ type: GET_PROGRESS_OF_USERS, payload: progress });
          callback && callback(progress);
        } else {
          dispatch(
            fetchError("There was a problem loading progress. Please try again or contact support.")
          );
          callback && callback(undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

export const getMyUnrespondedMessageCount = (callback?: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/MyUnrespondedMessageCount")
      .then((response) => {
        if (response.status === 200) {
          dispatch({
            type: GET_MY_UNRESPONDED_MESSAGE_COUNT,
            payload: response.data.UnrespondedMessageCount,
          });
          callback && callback(true);
        } else {
          callback && callback(false);
        }
      })
      .catch((error) => {
        console.log(error);
        callback && callback(false);
      });
  };
};

export const getFitForWorkOfTeam = (
  teamId: string,
  page: number,
  limit = 20,
  callback?: (userFitnessList: UserFitnessStatus[] | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get(`/api/FitForWork/FitForWorkOfTeam?teamId=${teamId}&pageIndex=${page}&pageSize=${limit}`)
      .then((response) => {
        callback && callback(response.data.Values);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

export const getTeamFitForWork = (
  page: number,
  limit = 20,
  callback?: (userFitnessList: UserFitnessStatus[] | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get(`/api/FitForWork/TeamFitForWork?pageIndex=${page}&pageSize=${limit}`)
      .then((response) => {
        callback && callback(response.data.Values);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

export const getMyFitForWork = (
  callback?: (userFitness: UserFitnessStatus | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/FitForWork/MyFitForWork")
      .then((response) => {
        callback && callback(response.data);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};
export const getTrainingOfTeam = (
  teamId: string,
  page: number,
  limit = 20,
  callback?: (userTrainingList: UserSkill[] | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get(`/api/Training/TrainingOfTeam?teamId=${teamId}&pageIndex=${page}&pageSize=${limit}`)
      .then((response) => {
        callback && callback(response.data.Values);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

export const getTeamTraining = (
  page: number,
  limit = 20,
  callback?: (userTrainingList: UserSkill[] | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get(`/api/Training/TeamTraining?pageIndex=${page}&pageSize=${limit}`)
      .then((response) => {
        callback && callback(response.data.Values);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error) {
          dispatch(fetchError(getDisplayErrorMessage(error)));
          callback && callback(undefined);
        }
      });
  };
};

export const getMyTraining = (callback?: (training: UserSkill | undefined) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/Training/MyTraining")
      .then((response) => {
        callback && callback(response.data);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

export const getTrainingOfUser = (
  userId: string,
  callback?: (userTrainingList: UserSkillItem[] | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get(`/api/Training/UserTrainingDetail?userId=${userId}`)
      .then((response) => {
        let sortedList: UserSkillItem[] = response.data.Values;
        sortedList = sortedList.sort(sortUserSkills);
        callback && callback(sortedList);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

export const setAllUsers = (users: OrgUser[]) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch({ type: GET_ALL_USERS, payload: users });
  };
};

export const updateUserOptInRoles = (
  userId: string,
  roleIds: string[],
  callback?: (result: boolean) => void,
  silent?: boolean
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .put(`api/admin/Users/${userId}/OptInRoles`, roleIds)
      .then((response) => {
        if (response.status === 200) {
          dispatch({ type: UPDATE_USER_OPT_IN_ROLES, payload: { userId, optInRoleIds: roleIds } });
          if (!silent) dispatch(showMessage("Opt-in roles updated."));
          callback && callback(true);
        } else if (response.status === 202) {
          dispatch({ type: UPDATE_USER_OPT_IN_ROLES, payload: { userId, optInRoleIds: roleIds } });
          if (!silent) dispatch(showMessage("Opt-in roles updated."));
          callback && callback(true);
        } else {
          if (!silent)
            dispatch(
              fetchError("Something went wrong, Please try again! Status:" + response.status)
            );
          callback && callback(false);
        }
      })
      .catch((error) => {
        if (!silent && (axios.isAxiosError(error) || error instanceof Error))
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(false);
      });
  };
};

export const updateUserLocations = (
  userId: string,
  locationIds: string[],
  callback?: (result: boolean) => void,
  silent?: boolean
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .put(`api/admin/Users/${userId}/LNIs`, locationIds)
      .then((response) => {
        if (response.status === 200) {
          dispatch({ type: UPDATE_USER_LOCATIONS, payload: { userId, locationIds } });
          if (!silent) dispatch(showMessage("Location changes updated."));
          callback && callback(true);
        } else if (response.status === 202) {
          dispatch({ type: UPDATE_USER_LOCATIONS, payload: { userId, locationIds } });
          if (!silent) dispatch(showMessage("Location changes updated."));
          callback && callback(true);
        } else {
          if (!silent)
            dispatch(
              fetchError("Something went wrong, Please try again! Status:" + response.status)
            );
          callback && callback(false);
        }
      })
      .catch((error) => {
        if (!silent && (axios.isAxiosError(error) || error instanceof Error))
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(false);
      });
  };
};

export const getAllUsers = (callback?: (users: OrgUser[] | undefined) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/admin/Users")
      .then((response) => {
        if (response.status === 200) {
          const users: OrgUser[] = response.data;
          dispatch({ type: GET_ALL_USERS, payload: users });
          callback && callback(users);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + response.status));
          callback && callback(undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

const statusOrder: Record<string, number> = {
  Expired: 1,
  Valid: 2,
  Unknown: 3,
};

const sortUserSkills = (a: UserSkillItem, b: UserSkillItem) => {
  const statusA = statusOrder[a.ExpiryStatus] ?? 99;
  const statusB = statusOrder[b.ExpiryStatus] ?? 99;

  if (statusA !== statusB) return statusA - statusB;

  const dateA = normalizeDate(a.ExpirationDate);
  const dateB = normalizeDate(b.ExpirationDate);

  // Empty date goes last
  if (!dateA && dateB) return 1;
  if (dateA && !dateB) return -1;

  // Compare dates when both exist
  if (dateA && dateB) {
    const dateCompare = dateA.localeCompare(dateB);
    if (dateCompare !== 0) return dateCompare;
  }

  // Fallback: compare SkillName
  return (a.SkillName ?? "").localeCompare(b.SkillName ?? "");
};

export const getMyTrainingDetail = (
  callback?: (userTrainingList: UserSkillItem[] | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/Training/MyTrainingDetail")
      .then((response) => {
        let sortedList: UserSkillItem[] = response.data.Values;
        sortedList = sortedList.sort(sortUserSkills);
        callback && callback(sortedList);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callback && callback(undefined);
      });
  };
};

export async function requestDownloadProgressCSVForOrgTeam(): Promise<
  CSVExportStatusResponse | undefined
> {
  const response = await jwtAxios.post("/api/CsvExport/TeamProgress");

  if (response.status === 202) {
    return response.data as CSVExportStatusResponse;
  } else {
    throw new Error("Failed to request CSV export. Please try again later.");
  }
}

export async function requestDownloadProgressCSVForVirtualTeam(
  teamId: string
): Promise<CSVExportStatusResponse | undefined> {
  const response = await jwtAxios.post(`/api/CsvExport/ProgressOfTeam?teamId=${teamId}`);

  if (response.status === 202) {
    return response.data as CSVExportStatusResponse;
  } else {
    throw new Error("Failed to request CSV export. Please try again later.");
  }
}

export async function getExportResult(instanceId: string, teamName: string) {
  const response = await jwtAxios.get(`/api/CsvExport/ExportResult?instanceId=${instanceId}`);
  if (response.status === 200) {
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
    const filename = `${teamName}.csv`;
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return {
      InstanceId: instanceId,
      Status: "Complete",
    };
  } else if (response.status === 202) {
    // Accepted response indicates the request is being processed
    console.log("Accepted response indicates the request is being processed");
    return response.data;
  } else {
    throw new Error("Failed to get CSV export result. Please try again later.");
  }
}
